import { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AgentFilterContext from './AgentFilterContext';

/**
 * An Agent 360 / User 360 deep link into a module page whose data follows the module's agent /
 * user selection (AgentFilterContext): the link's agent and person become that selection, so the
 * page's own Filters bar shows them as removable chips and every request the page makes carries
 * them, and the params leave the URL.
 *
 *   agents  `?agent_id=` or `?agent=` — an Atlas id `<prefix>:<name>` (the prefix is dropped) or
 *           the bare name; an Atlas id on another surface is not this module's and is ignored
 *   users   `?user=`, `?person=` or `?user_email=`
 */
export const AGENT_PARAMS = ['agent_id', 'agent'];
export const USER_PARAMS = ['user', 'person', 'user_email'];

const split = (v) => String(v || '').split(',').map((x) => x.trim()).filter(Boolean);

/** The link's selection for a module whose Atlas ids start `<prefix>:`, or null when it names none. */
export function linkedSelection(params, prefix) {
  const rawAgents = split(AGENT_PARAMS.map((k) => params.get(k)).find(Boolean));
  const users = split(USER_PARAMS.map((k) => params.get(k)).find(Boolean));
  if (!rawAgents.length && !users.length) return null;
  const agents = rawAgents.flatMap((a) => {
    if (prefix && a.startsWith(`${prefix}:`)) return [a.slice(prefix.length + 1)];
    // another surface's Atlas id (`gcp:…`) is not this module's agent
    if (/^[a-z][a-z0-9-]*:/.test(a)) return [];
    return [a];
  });
  return { agents, users };
}

/** Adopt a deep link into the module selection once (no-op without a provider or a link). */
export default function useLinkedSelection(prefix, { enabled = true } = {}) {
  const ctx = useContext(AgentFilterContext);
  const [params, setParams] = useSearchParams();
  const link = enabled && ctx ? linkedSelection(params, prefix) : null;
  const key = link ? JSON.stringify(link) : '';
  const setFilters = ctx?.setFilters;
  useEffect(() => {
    if (!key || !setFilters) return;
    setFilters(JSON.parse(key));
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      [...AGENT_PARAMS, ...USER_PARAMS].forEach((k) => next.delete(k));
      return next;
    }, { replace: true });
    // the link is consumed once; setFilters / setParams change with every pick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
